'use client';

/**
 * ChatAssistant Component
 * 
 * REQUIREMENTS SUMMARY:
 * 1. Main wrapper component that combines ChatBubble and ChatWindow
 * 2. Provides feedback submission capability for AI responses
 * 3. This is the component to add to the root layout
 */

import React, { useEffect, useRef, useState } from 'react';
import { useChat } from '@/hooks/chat/useChat';
import { useUserRole } from '@/context/UserRoleContext';
import { useOrganization } from '@/context/OrganizationContext';
import { useIsMobile } from '@/hooks/ui/useMediaQuery';
import { useAuth } from '@/context/AuthContext';
import { usePathname } from 'next/navigation';
import { FEEDBACK_RATING_CHAT_CONTEXT } from '@/components/feedback/FeedbackPromptCard';
import { ChatBubble } from './ChatBubble';
import { ChatWindow } from './ChatWindow';
import { isSettingsFocusPath } from '@/utils/navigation/settingsFocusMode';

/** Breathing room for the chat window to open and render before the first message. */
const AUTO_MESSAGE_SEND_DELAY_MS = 300;

/** An autoMessage waiting for the assistant to be idle enough to receive it. */
interface PendingAutoMessage {
  message: string;
  /** Clear the thread first, so this message starts its own conversation. */
  startFresh: boolean;
}

export function ChatAssistant() {
  const { user, isLoading: authLoading } = useAuth();
  const { currentUser } = useUserRole();
  const {
    currentOrganization,
    isLoading: orgLoading,
    needsOrganizationSelection,
  } = useOrganization();
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const {
    messages,
    conversations,
    currentConversationId,
    isOpen,
    isLoading,
    isSending,
    error,
    suggestions,
    sendMessage,
    startNewConversation,
    loadConversation,
    deleteConversation,
    toggleOpen,
    setIsOpen,
    clearError,
    stopGeneration,
    submitFeedback,
    confirmNavigation,
    draftKey,
    draft,
    prefillDraft,
    clearDraft,
    loadAttachment,
    retrySupportAttachments,
  } = useChat();

  // An autoMessage is QUEUED rather than fired on a one-shot timer: useChat's
  // sendMessage no-ops while isSending is true, so a message that arrives
  // mid-response would otherwise be silently swallowed (and, for the rating
  // prompt, the click has already been recorded as 'opened' — the user would be
  // counted as abandoning a conversation they never saw).
  const [pendingAutoMessage, setPendingAutoMessage] = useState<PendingAutoMessage | null>(null);

  // sendMessage's identity changes on every streamed token. Calling it through a
  // ref keeps the flush effect below from restarting its timer mid-stream, and
  // guarantees the message is sent by the LATEST sendMessage — the one that has
  // the cleared conversation id after startNewConversation().
  const sendMessageRef = useRef(sendMessage);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  // Listen for custom event to open chat programmatically with context
  useEffect(() => {
    const handleOpenChat = (event: Event) => {
      const customEvent = event as CustomEvent<{ context?: string; autoMessage?: string; draft?: string }>;
      const context = customEvent.detail?.context;
      const autoMessage = customEvent.detail?.autoMessage;
      // `draft` only fills the input box; nothing is sent until the person presses send.
      const draftText = customEvent.detail?.draft;

      // Open regardless of current state — an already-open window must still
      // receive the autoMessage instead of dropping it on the floor.
      if (!isOpen) {
        setIsOpen(true);
      }

      if (!autoMessage) {
        if (typeof draftText === 'string' && draftText.trim()) prefillDraft(draftText);
        return;
      }

      setPendingAutoMessage({
        message: autoMessage,
        // The rating always begins in its own thread. currentConversationId
        // survives the whole session, so without this the question would be
        // injected into whatever unrelated conversation was already loaded.
        startFresh: context === FEEDBACK_RATING_CHAT_CONTEXT,
      });
    };

    window.addEventListener('openChatAssistant', handleOpenChat as EventListener);
    return () => {
      window.removeEventListener('openChatAssistant', handleOpenChat as EventListener);
    };
  }, [isOpen, setIsOpen, prefillDraft]);

  // Deliver the queued autoMessage once the assistant is idle.
  useEffect(() => {
    if (!pendingAutoMessage) return;
    // Mid-response: wait. This effect re-runs when isSending flips back to false.
    if (isSending) return;

    if (pendingAutoMessage.startFresh) {
      // Clear the thread FIRST and send on the next pass — sending in this same
      // tick would still carry the previous conversation id.
      startNewConversation();
      setPendingAutoMessage({ ...pendingAutoMessage, startFresh: false });
      return;
    }

    const timer = setTimeout(() => {
      setPendingAutoMessage(null);
      sendMessageRef.current(pendingAutoMessage.message);
    }, AUTO_MESSAGE_SEND_DELAY_MS);

    return () => clearTimeout(timer);
  }, [pendingAutoMessage, isSending, startNewConversation]);

  // Hide chatbot on auth screens or if user is not authenticated
  const isAuthScreen = pathname?.startsWith('/signin') || 
                       pathname?.startsWith('/register') || 
                       pathname?.startsWith('/login') || 
                       pathname?.startsWith('/signup') ||
                       pathname?.startsWith('/forgot-password') ||
                       pathname?.startsWith('/verify-email') ||
                       pathname?.startsWith('/reset-password') ||
                       pathname?.startsWith('/staff-invitation') ||
                       pathname?.startsWith('/onboarding');

  // Hide ChatBubble FAB when already in the chatbot webview (mobile app WebView loads /mobile/chat).
  // Other mobile webviews (attendance, announcements, etc.) keep the FAB.
  const isMobileChatPage = pathname?.startsWith('/mobile/chat');

  // Hide on public-facing pages (program registration, private lesson booking, etc.)
  // /join/ is the family's join-link flow (they are signed in after the code step);
  // /join-poster/ is the printable QR poster staff hang at events.
  const isPublicPage = pathname?.startsWith('/programs/') ||
                       pathname?.startsWith('/store/') ||
                       pathname?.startsWith('/team-placement-reveal/') ||
                       pathname?.startsWith('/join/') ||
                       pathname?.startsWith('/join-poster/');

  // Hide on team builder pages (manual and AI) — they have their own fullscreen layout
  const isTeamBuilderPage = /\/team-builder\/[^/]+/.test(pathname || '');

  // Hide on kiosk page — clean full-screen kiosk for coach clock-in/out
  const isKioskPage = pathname === '/kiosk';

  // Hide on web Team Chat (#2563) — it is a full-screen chat app in its own tab,
  // and a second floating chat bubble on top of it reads as part of that app.
  const isTeamChatPage = pathname?.startsWith('/team-chat');

  // Hide on Teams Manager — it has its own assistant button in the toolbar
  const isTeamsManagerPage = pathname?.startsWith('/dashboard/teams-manager');

  // Hide on gym settings — the focus-mode header has its own assistant button
  const isSettingsFocusPage = isSettingsFocusPath(pathname);

  // Hide entirely when rendered inside the mobile app's WebView — the mobile app has its own AI tab
  const isMobileWebView = typeof window !== 'undefined' && !!(window as any).__isCheerVueMobile;

  if (authLoading) return null;
  // OrganizationSelectionWrapper owns the whole screen twice before a gym is in
  // play: the "Loading your organizations..." loader, then the gym picker. The
  // launcher lives in the ROOT layout, so without this it paints over both.
  // Both halves read the same OrganizationContext values that wrapper's own two
  // branches do. It is only mounted under (admin), so on the handful of
  // authenticated routes outside that group this hides the launcher with no
  // picker on screen — deliberate: in both branches that raise the picker the
  // assistant has no working gym to answer about (none chosen, or the chosen
  // one is erroring).
  const isOrganizationGateShowing =
    (orgLoading && !currentOrganization) || needsOrganizationSelection;
  if (user && isOrganizationGateShowing) return null;
  // #3392 — billing-only re-entry gym selected: hide the assistant entirely.
  // The assistant's own org gate refuses inactive parents, so a visible
  // launcher would only hand a departed parent an error on a money surface.
  if (currentOrganization?.billingOnly === true) return null;
  if (!user || isAuthScreen || isMobileChatPage || isPublicPage || isTeamBuilderPage || isKioskPage || isTeamChatPage || isMobileWebView) return null;

  // Hide the FAB on pages that already have an assistant button (settings
  // header, Teams Manager toolbar). ChatWindow stays mounted so those
  // buttons can still open it via openChatAssistant.
  const hideBubble = isTeamsManagerPage || isSettingsFocusPage;
  
  return (
    <>
      {/* Floating bubble — hidden on pages that have their own assistant button */}
      {!hideBubble && (
        <ChatBubble
          isOpen={isOpen}
          onClick={toggleOpen}
        />
      )}
      
      {/* Chat window */}
      <ChatWindow
        isOpen={isOpen}
        messages={messages}
        conversations={conversations}
        currentConversationId={currentConversationId}
        isLoading={isLoading}
        isSending={isSending}
        error={error}
        userFirstName={currentUser?.firstName}
        userType={currentUser?.userType}
        userRole={currentUser?.activeRole}
        onSendMessage={sendMessage}
        draftKey={draftKey}
        draft={draft}
        onDraftDone={clearDraft}
        loadAttachment={loadAttachment}
        onRetrySupportAttachments={retrySupportAttachments}
        onStopGeneration={stopGeneration}
        onNewConversation={startNewConversation}
        onLoadConversation={loadConversation}
        onDeleteConversation={deleteConversation}
        onClose={toggleOpen}
        onClearError={clearError}
        onFeedback={submitFeedback}
        onConfirmNavigation={confirmNavigation}
        isMobile={isMobile}
        suggestions={suggestions}
        organizationName={currentOrganization?.name}
      />
    </>
  );
}

export default ChatAssistant;
